import { useEffect, useMemo } from 'react'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { fetchDestinationTags } from 'actions/PlaceActions'
import useQueryParams from 'hooks/useQueryParams'
import moment from 'moment'

interface UseDestinationTagsResult {
  tags: Array<{ id: string; name: string }>
  fetching: boolean
}

interface Options {
  disabled?: boolean
}

/**
 * Hook to fetch and access destination tags from Redux store
 * @param destinationId - The destination ID to fetch tags for
 * @param options - Optional configuration including disabled flag
 * @returns Object containing tags array and fetching status
 */
function useDestinationTags(
  destinationId: string | undefined,
  options: Options = {},
): UseDestinationTagsResult {
  const dispatch = useAppDispatch()
  const queryParams = useQueryParams()

  const travelStartDate = useMemo(() => {
    const checkInDate = queryParams.get('checkIn')
    return checkInDate ? moment(checkInDate).utc().startOf('day').toISOString() : undefined
  }, [queryParams])

  const travelEndDate = useMemo(() => {
    const checkOutDate = queryParams.get('checkOut')
    return checkOutDate ? moment(checkOutDate).utc().startOf('day').toISOString() : undefined
  }, [queryParams])

  const cacheKey = useMemo(() => {
    if (!destinationId) return undefined
    const datePart = [travelStartDate, travelEndDate].filter(Boolean).join(':')
    return datePart ? `${destinationId}:${datePart}` : destinationId
  }, [destinationId, travelStartDate, travelEndDate])

  const tagData = useAppSelector(state => {
    if (!cacheKey || options.disabled) {
      return undefined
    }
    return state.destination.destinationTags[cacheKey]
  })

  useEffect(() => {
    if (!destinationId || options.disabled) {
      return
    }

    dispatch(fetchDestinationTags(destinationId, { travelStartDate, travelEndDate, travelDateOverlap: true }))
  }, [dispatch, destinationId, travelStartDate, travelEndDate, options.disabled])

  return {
    tags: tagData?.tags ?? [],
    fetching: tagData?.fetching ?? false,
  }
}

export default useDestinationTags
